import { useState, useEffect } from 'react';
import { GOLD, SILVER, DARK_BG, CARD_BG, BORDER, TEXT, MUTED, GREEN, RED } from './constants';
import { today, uid, loadLS, saveLS, LS_KEYS, fetchMetalPrices } from './utils';

import Login       from './components/Login';
import Dashboard   from './components/Dashboard';
import TransactionsTab from './components/Transactions';
import InvoicesTab from './components/Invoices';
import InventoryTab from './components/Inventory';
import KPIReports  from './components/KPIReports';
import Settings    from './components/Settings';
import { MetalsTicker } from './components/UI';

const DEFAULT_PASSWORD = 'AGC2025!';
const DEFAULT_SETTINGS = { password: DEFAULT_PASSWORD };
const TABS = [
  { id: 'dashboard',    label: 'Dashboard' },
  { id: 'transactions', label: 'Transactions' },
  { id: 'invoices',     label: 'Invoices' },
  { id: 'inventory',    label: 'Inventory' },
  { id: 'kpis',         label: 'KPI Reports' },
  { id: 'settings',     label: '⚙ Settings' },
];

export default function App() {
  const [authed,       setAuthed]       = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [invoices,     setInvoices]     = useState([]);
  const [settings,     setSettings]     = useState(DEFAULT_SETTINGS);
  const [metalPrices,  setMetalPrices]  = useState({ Gold: null, Silver: null, Platinum: null, Palladium: null, ok: false, timestamp: '—' });
  const [activeTab,    setActiveTab]    = useState('dashboard');
  const [lastBackup,   setLastBackup]   = useState(null);

  // Load from localStorage on mount
  useEffect(() => {
    const tx  = loadLS(LS_KEYS.transactions, []);
    const inv = loadLS(LS_KEYS.invoices, []);
    const set = loadLS(LS_KEYS.settings, DEFAULT_SETTINGS);
    const lb  = loadLS(LS_KEYS.lastBackup, null);
    setTransactions(tx);
    setInvoices(inv);
    setSettings(set);
    setLastBackup(lb);
  }, []);

  // Persist transactions
  useEffect(() => { saveLS(LS_KEYS.transactions, transactions); }, [transactions]);
  useEffect(() => { saveLS(LS_KEYS.invoices, invoices); },     [invoices]);
  useEffect(() => { saveLS(LS_KEYS.settings, settings); },     [settings]);

  // Live metals prices
  useEffect(() => {
    fetchMetalPrices().then(setMetalPrices);
    const t = setInterval(() => fetchMetalPrices().then(setMetalPrices), 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  // Transaction CRUD
  function addTransaction(tx) {
    const entry = { ...tx, id: uid(), createdAt: new Date().toISOString() };
    setTransactions((p) => [entry, ...p]);
    return entry;
  }
  function deleteTransaction(id) {
    setTransactions((p) => p.filter((t) => t.id !== id));
  }

  // Invoice CRUD — also posts line items as transactions
  function addInvoice(inv) {
    const invEntry = { ...inv, id: uid(), createdAt: new Date().toISOString() };
    setInvoices((p) => [invEntry, ...p]);
    (inv.lineItems || []).forEach((li) => {
      addTransaction({
        date:          inv.date,
        direction:     inv.direction,
        channel:       inv.channel,
        vendor:        inv.vendor,
        invoiceNumber: inv.invoiceNumber,
        invoiceId:     invEntry.id,
        category:      li.category,
        description:   li.description,
        metal:         li.metal,
        qty:           li.qty,
        unitPrice:     li.unitPrice,
        totalAmount:   li.qty * li.unitPrice,
        weight:        li.weight || 0,
        notes:         `Invoice: ${inv.invoiceNumber}`,
        source:        'invoice',
      });
    });
  }

  if (!authed) {
    return <Login onSuccess={() => setAuthed(true)} storedPassword={settings.password} />;
  }

  return (
    <div style={{ background: DARK_BG, minHeight: '100vh', color: TEXT, fontFamily: "'DM Mono', monospace" }}>

      {/* ── HEADER ── */}
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 32px', background: CARD_BG,
        borderBottom: `1px solid ${BORDER}`, position: 'sticky', top: 0, zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 900, color: GOLD, lineHeight: 1 }}>AGC</span>
          <div>
            <div style={{ color: MUTED, fontSize: 9, letterSpacing: 3 }}>ATLANTA GOLD & COIN</div>
            <div style={{ color: MUTED, fontSize: 8, letterSpacing: 2, opacity: 0.6 }}>OPERATIONS · JOHNS CREEK, GA</div>
          </div>
        </div>

        <MetalsTicker prices={metalPrices} />

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          <button onClick={() => setActiveTab('settings')} style={{
            background: 'transparent', border: `1px solid ${BORDER}`, color: GOLD,
            padding: '6px 14px', borderRadius: 6, cursor: 'pointer',
            fontSize: 11, fontFamily: "'DM Mono', monospace", letterSpacing: 0.5,
          }}>
            ↓ Backup / Settings
          </button>
          {lastBackup && <span style={{ color: MUTED, fontSize: 9 }}>Last backup: {lastBackup}</span>}
        </div>
      </header>

      {/* ── NAV ── */}
      <nav style={{
        display: 'flex', gap: 2, padding: '0 32px',
        background: CARD_BG, borderBottom: `1px solid ${BORDER}`,
      }}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '13px 20px', background: 'transparent', border: 'none',
              color: activeTab === tab.id ? TEXT : MUTED,
              cursor: 'pointer', fontSize: 12, letterSpacing: 0.5,
              borderBottom: `2px solid ${activeTab === tab.id ? GOLD : 'transparent'}`,
              fontFamily: "'DM Mono', monospace", transition: 'all 0.15s',
            }}
          >{tab.label}</button>
        ))}
      </nav>

      {/* ── CONTENT ── */}
      <main style={{ padding: '28px 32px', maxWidth: 1700, margin: '0 auto' }}>
        {activeTab === 'dashboard'    && <Dashboard   transactions={transactions} metalPrices={metalPrices} />}
        {activeTab === 'transactions' && <TransactionsTab transactions={transactions} onAdd={addTransaction} onDelete={deleteTransaction} />}
        {activeTab === 'invoices'     && <InvoicesTab  invoices={invoices} onAdd={addInvoice} />}
        {activeTab === 'inventory'    && <InventoryTab  transactions={transactions} metalPrices={metalPrices} />}
        {activeTab === 'kpis'         && <KPIReports    transactions={transactions} />}
        {activeTab === 'settings'     && (
          <Settings
            transactions={transactions}
            invoices={invoices}
            settings={settings}
            onSettingsChange={setSettings}
            lastBackup={lastBackup}
            setLastBackup={setLastBackup}
          />
        )}
      </main>
    </div>
  );
}
